import { useState, useEffect, SyntheticEvent } from "react";
import { useParams } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import ProductRelated from "../../components/ProductRelated";
import ProductDetailFooter from "../../components/ProductDetailFooter";
import EmptyState from "../../components/EmptyState";
import { Skeleton } from "../../components/Skeleton";
import { getDetailProduct } from "../../server/product";
import { formatVND } from "../../contants/formatVND";
import { toSlug, getUserStatus, cleanUrl } from "../../contants";

export default function ProductDetail() {
  const { id } = useParams();
  const role = getUserStatus();

  const [data, setData] = useState<any>(null);
  const [load, setLoad] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);

  const handleImageError = (event: SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    img.onerror = null;
    img.src = "/no-image.jpg";
  };

  useEffect(() => {
    async function loadData() {
      if (id) {
        setLoad(true);
        const res = await getDetailProduct(id);
        setData(res);
        setLoad(false);
      }
    }
    loadData();
  }, [id]);

  if (load) return <ProductDetailSkeleton />;

  if (!data?.id)
    return (
      <EmptyState
        title="Không tìm thấy sản phẩm"
        description="Sản phẩm có thể đã bị xóa hoặc không tồn tại."
      />
    );

  return (
    <>
      <div className="max-w-7xl row pl-4" >
        <Breadcrumb
          items={[
            { label: "Trang chủ", href: "/" },
            {
              label: data?.categoryName,
              href:
                "/danh-muc-san-pham/" +
                toSlug(data?.categoryName ?? "") +
                "/" +
                data?.categoryId,
            },
            {
              label: data?.productName,
            },
          ]}
        />
      </div>

      <div className="shop-container ">
        <div className="container max-w-7xl">

          {/* PRODUCT TITLE */}


          {/* MAIN CONTENT */}
          <div className="mt-10 grid max-w-7xl grid-cols-1 gap-5 lg:grid-cols-12 lg:items-start">
            {/* LEFT IMAGE */}
            <div className="product-gallery lg:col-span-4 flex flex-col gap-4 h-full">
              <div className="relative rounded-2xl border border-slate-200 bg-white p-3 shadow-sm flex-1 lg:min-h-[460px]">
                <figure
                  className="woocommerce-product-gallery__wrapper cursor-zoom-in overflow-hidden rounded-xl bg-slate-50 flex items-center justify-center h-[220px] sm:h-[320px] lg:h-[420px]"
                  onClick={() => setLightboxOpen(true)}
                >
                  <img
                    src={cleanUrl(data?.images?.[imageIndex]) || "/no-image.jpg"}
                    alt={data?.productName}
                    className="max-h-full w-full rounded-xl object-contain"
                    onError={handleImageError}
                  />
                </figure>

                {data?.images?.length > 1 && (
                  <>
                    <button
                      className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/80 px-2 py-1 text-sm font-bold text-slate-900 shadow"
                      onClick={() =>
                        setImageIndex((prev: number) =>
                          (prev - 1 + data.images.length) % data.images.length
                        )
                      }
                    >
                      ‹
                    </button>
                    <button
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/80 px-2 py-1 text-sm font-bold text-slate-900 shadow"
                      onClick={() =>
                        setImageIndex((prev: number) =>
                          data?.images?.length
                            ? (prev + 1) % data.images.length
                            : 0
                        )
                      }
                    >
                      ›
                    </button>
                  </>
                )}
              </div>

              {/* Thumbnails */}
              <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                {data?.images?.map((item: any, idx: number) => (
                  <button
                    key={item}
                    className={`overflow-hidden rounded-xl border transition hover:shadow ${idx === imageIndex
                        ? "border-blue-500 ring-2 ring-blue-100"
                        : "border-slate-200"
                      }`}
                    onClick={() => setImageIndex(idx)}
                  >
                    <img
                      src={cleanUrl(item) || "/no-image.jpg"}
                      alt=""
                      className="h-20 w-full object-cover"
                      onError={handleImageError}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* PRODUCT INFORMATION */}
            <div className="summary entry-summary lg:col-span-5 h-full">
              <div className="h-full rounded-2xl border border-slate-200 bg-white p-6 shadow-sm flex flex-col lg:min-h-[460px]">
                <h1 className="mb-4 text-2xl font-semibold text-slate-900">
                  {data?.productName}
                </h1>
                <p className="price product-page-price text-xl text-blue-600">
                  <bdi>{formatVND(data?.price ?? 0)}</bdi>

                  {!role && (
                    <>
                      <br />
                      <small>
                        <a href="/dang-nhap">Đăng nhập để xem giá đại lý</a>
                      </small>
                    </>
                  )}
                </p>

                <ul className="mt-6 space-y-3 text-slate-700 flex-1">
                  <li className="flex items-start justify-between rounded-lg bg-slate-50 px-4 py-3">
                    <span>Mã sản phẩm</span>
                    <strong>{data?.productCode}</strong>
                  </li>
                  <li className="flex items-start justify-between rounded-lg bg-slate-50 px-4 py-3">
                    <span>Thương hiệu</span>
                    <strong>{data?.brandName}</strong>
                  </li>
                  <li className="flex items-start justify-between rounded-lg bg-slate-50 px-4 py-3">
                    <span>Số lượng</span>
                    <strong>{data?.quantity > 0 ? data?.quantity : "Hết hàng"}</strong>
                  </li>
                </ul>
              </div>
            </div>

            {/* RIGHT INFO BOX */}
            <div className="lg:col-span-3 h-full">
              <div className="h-full rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 via-white to-white p-6 shadow-sm flex flex-col gap-3 lg:min-h-[460px]">
                <div className="text-lg font-semibold text-blue-900">
                  Chiết khấu - Chính sách
                </div>
                <ul className="space-y-3 text-slate-700">
                  <li className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-blue-500" />
                    Hàng phân phối chính hãng
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-blue-500" />
                    Hóa đơn chứng từ đầy đủ
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-blue-500" />
                    Chiết khấu khi thanh toán ngay
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* RELATED + FOOTER */}
          <ProductRelated id={id ?? ""} />
          {data?.id && (
            <ProductDetailFooter
              description={data?.decription ?? ""}
              title={data?.productName ?? ""}
            />
          )}
        </div>
      </div>

      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[9999] flex flex-col bg-black/80 backdrop-blur-sm"
          onClick={() => setLightboxOpen(false)}
        >
          <div className="relative mx-auto flex w-full max-w-6xl flex-1 items-center justify-center px-4 py-6">
            <button
              className="absolute right-6 top-4 rounded-full bg-white/90 px-3 py-2 text-sm font-semibold text-slate-900 shadow"
              onClick={(e) => {
                e.stopPropagation();
                setLightboxOpen(false);
              }}
            >
              Đóng
            </button>

            <button
              className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/80 px-3 py-2 text-sm font-bold text-slate-900 shadow"
              onClick={(e) => {
                e.stopPropagation();
                setImageIndex((prev) =>
                  data?.images && data.images.length
                    ? (prev - 1 + data.images.length) % data.images.length
                    : 0
                );
              }}
            >
              ‹
            </button>

            <button
              className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/80 px-3 py-2 text-sm font-bold text-slate-900 shadow"
              onClick={(e) => {
                e.stopPropagation();
                setImageIndex((prev) =>
                  data?.images && data.images.length
                    ? (prev + 1) % data.images.length
                    : 0
                );
              }}
            >
              ›
            </button>

            <img
              src={cleanUrl(data?.images?.[imageIndex]) || "/no-image.jpg"}
              alt={data?.productName}
              className="max-h-[80vh] w-auto max-w-full rounded-2xl object-contain shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              onError={handleImageError}
            />
          </div>

          <div className="mx-auto mb-4 flex w-full max-w-6xl gap-2 overflow-x-auto px-4 pb-4 scroll-rounded">
            {data?.images?.map((img: string, idx: number) => (
              <button
                key={`${img}-${idx}`}
                className={`h-16 w-20 flex-shrink-0 overflow-hidden rounded-lg border ${idx === imageIndex
                    ? "border-white"
                    : "border-white/30 opacity-70"
                  }`}
                onClick={(e) => {
                  e.stopPropagation();
                  setImageIndex(idx);
                }}
              >
                <img
                  src={cleanUrl(img) || "/no-image.jpg"}
                  alt={`${data?.productName} ${idx + 1}`}
                  className="h-full w-full object-cover"
                  onError={handleImageError}
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function ProductDetailSkeleton() {
  return (
    <div className="shop-container">
      <div className="container max-w-7xl">
        <div className="mt-6 grid max-w-7xl grid-cols-1 gap-5 lg:grid-cols-12 lg:items-start">
          <div className="product-gallery lg:col-span-4 flex flex-col gap-4 h-full">
            <div className="relative rounded-2xl border border-slate-200 bg-white p-3 shadow-sm flex-1 lg:min-h-[460px]">
              <Skeleton className="h-[220px] sm:h-[320px] lg:h-[420px] w-full rounded-xl" />
            </div>
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
              {Array.from({ length: 4 }).map((_, idx) => (
                <Skeleton key={idx} className="h-20 w-full rounded-xl" />
              ))}
            </div>
          </div>

          <div className="summary entry-summary lg:col-span-5 h-full">
            <div className="h-full rounded-2xl border border-slate-200 bg-white p-6 shadow-sm flex flex-col lg:min-h-[460px]">
              <Skeleton className="h-6 w-2/3" />
              <Skeleton className="mt-4 h-5 w-1/3" />
              <div className="mt-6 space-y-3">
                {Array.from({ length: 3 }).map((_, idx) => (
                  <Skeleton key={idx} className="h-12 w-full rounded-lg" />
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-3 h-full">
            <div className="h-full rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 via-white to-white p-6 shadow-sm flex flex-col gap-3 lg:min-h-[460px]">
              {Array.from({ length: 5 }).map((_, idx) => (
                <Skeleton key={idx} className="h-4 w-full" />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
